'use client';

import { Alert } from '@heroui/alert';
import { Button } from '@heroui/button';
import { CheckCircle, DollarSign, Users, Zap, Wallet, ArrowUpDown, PiggyBank } from 'lucide-react';

import { Feature } from '@/components/common/feature-variants';
import ContactUsCTA from '@/components/common/ContactCTA';
import { Link } from '@heroui/link';

export default function PricingPage() {
  return (
    <>
      <section className="section-md bg-gradient-to-b from-primary/20 to-background/5">
        <div className="container container-max">
          {/* Hero Section */}
          <div className="space-y-4 text-center">
            <h1 className="mb-6 font-bold text-transparent hero-title bg-clip-text bg-gradient-to-l from-primary to-secondary ">
              Transparent <span className="text-primary">Pricing</span>
            </h1>

            <p className="max-w-3xl mx-auto text-foreground/70">
              At PayStop, we believe in providing transparent, fair, and competitive pricing for all
              our services. Our platform offers a range of financial services designed to help local
              businesses and individuals manage their money easily and securely. Whether you’re a
              customer looking to make a deposit or withdrawal, or a business owner wishing to offer
              financial services through our agent network, PayStop offers affordable pricing to
              suit your needs.
            </p>
          </div>
        </div>
      </section>

      <section className="section-md">
        <div className="container container-md">
          {/* Customer Pricing */}
          <div className="space-y-8">
            <div className="flex items-center justify-center space-x-4">
              <h2 className="font-semibold title">Customer Pricing</h2>
            </div>
            <p className="max-w-3xl mx-auto text-center text-foreground/70">
              As a customer, you can access a variety of financial services, including depositing
              and withdrawing money, transferring funds, and more. The following fees apply to
              transactions conducted via the PayStop platform:
            </p>
            <div className="grid gap-16 md:grid-cols-3">
              {[
                {
                  title: 'Deposit Fees',
                  icon: DollarSign,
                  description:
                    'A small fee applies when you deposit money into your digital bank account, mobile wallet, or traditional bank account through a PayStop agent. This fee is typically a percentage of the deposit amount.',
                },
                {
                  title: 'Withdrawal Fees',
                  icon: Wallet,
                  description:
                    'Withdrawals made from your digital bank account or mobile wallet through a PayStop agent are subject to a transaction fee. This fee is typically a percentage of the withdrawal amount.',
                },
                {
                  title: 'Money Transfer Fees',
                  icon: ArrowUpDown,
                  description:
                    "When transferring money to other accounts, a fee is applied based on the transaction amount and the recipient's chosen method (digital bank account, mobile wallet, or traditional bank).",
                },
              ].map((item) => (
                <Feature
                  key={item.title} // Use the `title` as a unique key here
                  description={item.description}
                  icon={<item.icon className="w-6 h-6 text-primary" />}
                  title={item.title}
                  variant="simple"
                />
              ))}
            </div>
            <p className="text-sm italic text-center text-foreground/70">
              Note: The transaction fees are generally split between the PayStop agent and
              sub-agent, allowing them to earn a reward for processing your transactions.
            </p>
          </div>
        </div>
      </section>

      <section className="section-md bg-slate-50 dark:bg-background">
        <div className="container container-md">
          {/* Agent Pricing */}
          <div className="space-y-12">
            <div className="flex flex-col items-center justify-center space-x-4 space-y-5">
              <h2 className="font-semibold title">Agent Pricing</h2>
              <p className="max-w-3xl mx-auto text-center text-foreground/70">
                For agents offering services through the PayStop platform, we provide competitive
                rewards and fees. Here's a breakdown of the pricing structure for agents:
              </p>
            </div>

            <div className="grid gap-20 md:grid-cols-2">
              {[
                {
                  title: 'Agent Setup Fee',
                  icon: CheckCircle,
                  description:
                    'There is no setup fee to become a PayStop agent. Simply sign up, fund your account through a commercial bank, and start offering services to customers.',
                },
                {
                  title: 'Deposit and Withdrawal Fees',
                  icon: DollarSign,
                  description:
                    'Agents earn a percentage of the transaction fees when customers deposit or withdraw money via the PayStop platform. The fees are shared between the Super Agent and the Sub-Agent.',
                },
                {
                  title: 'Money Transfer Fees',
                  icon: ArrowUpDown,
                  description:
                    'Agents also earn a fee when facilitating money transfers for customers. These fees are typically a percentage of the transaction value, with the reward shared between the Super Agent and Sub-Agent.',
                },
                {
                  title: 'Funding Rewards',
                  icon: PiggyBank,
                  description:
                    'When agents fund their accounts through a commercial bank, they are rewarded with 0.3% of the funded amount. This reward helps agents offset their initial startup costs and encourages growth within the PayStop network.',
                },
              ].map((item) => (
                <Feature
                  key={item.title} // Use the `title` as a unique key here
                  description={item.description}
                  icon={<item.icon className="w-6 h-6 text-primary" />}
                  title={item.title}
                  variant="horizontal"
                />
              ))}
            </div>

            <div className="flex items-center justify-center w-full">
              <Alert
                description=" Other service fees may apply based on the transaction type and
                any additional services provided through PayStop. All fees
                will be clearly communicated at the time of each transaction."
                title="  Additional Fees"
              />
            </div>

            {/* <div className="p-4 m-auto text-xs border rounded-xl border-primary-100 bg-primary-50">
            <div className="flex gap-2">
              <h3 className="w-1/6 mb-2 font-semibold text-primary ">
                Additional Fees
              </h3>

              <p className=" text-foreground/70">
                Other service fees may apply based on the transaction type and
                any additional services provided through PayStop. All fees
                will be clearly communicated at the time of each transaction.
              </p>
            </div>
          </div> */}
          </div>
        </div>
      </section>

      <section className="section-md bg-gradient-to-r from-primary/20 to-primary/5">
        <div className="container container-max">
          {/* Transparent Pricing */}
          <div className="relative z-10 text-center">
            <h2 className="mb-4 font-semibold title">Transparent Pricing for Everyone</h2>
            <p className="max-w-3xl mx-auto text-foreground/70">
              We're committed to keeping our fees low and transparent so that businesses and
              customers can benefit from our services without unexpected costs. The exact fees for
              each transaction type will always be displayed before you confirm the transaction,
              allowing you to make informed decisions about your financial activities.
            </p>
          </div>
        </div>
      </section>

      <section className="section-md">
        <div className="container container-md">
          {/* Why Choose PayStop */}
          <div className="space-y-12">
            <h2 className="font-semibold text-center title">Why Choose PayStop?</h2>
            <div className="grid gap-20 md:grid-cols-2">
              {[
                {
                  title: 'Competitive Fees',
                  icon: DollarSign,
                  description:
                    'PayStop offers some of the most competitive fees in the industry, ensuring that both customers and agents can benefit from low-cost transactions and rewards.',
                },
                {
                  title: 'No Hidden Charges',
                  icon: CheckCircle,
                  description:
                    "We believe in clear, upfront pricing. All fees are communicated transparently, so you'll never be surprised by hidden charges.",
                },
                {
                  title: 'Flexible Payment Options',
                  icon: Zap,
                  description:
                    "Whether you're depositing or withdrawing money into a digital bank account, mobile wallet, or traditional bank account, PayStop provides you with affordable pricing for a range of financial services.",
                },
                {
                  title: 'Rewards for Agents',
                  icon: Users,
                  description:
                    'Local business owners and entrepreneurs who become PayStop agents are rewarded with transaction-based fees, helping to drive growth within their business while providing essential services to their community.',
                },
              ].map((item) => (
                <Feature
                  key={item.title} // Use the `title` as a unique key here
                  description={item.description}
                  icon={<item.icon className="w-6 h-6 text-primary" />}
                  title={item.title}
                  variant="horizontal"
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="">
        <div className="container container-max">
          <ContactUsCTA
            description={
              " If you have any questions about our pricing structure or need clarification on any of our fees, please don't hesitate to reach out to our team. We're happy to help!"
            }
            email={'  support@paystop.io'}
            office={'23 Business Avenue, City, Country'}
            phone={'+237 676962723'}
            showOffice={false}
            title={' Contact Us for More Information'}
          />
          {/* Contact Us */}
        </div>
      </section>
      <section className="section-md">
        <div className="container space-y-24 container-max">
          {/* Call to Action */}
          <div className="relative px-8 py-16 overflow-hidden bg-gradient-to-b from-primary/20 to-primary/5 rounded-3xl">
            <div className="relative z-10 space-y-4 text-center">
              <h2 className="font-bold text-primary title">
                Your Financial Services, Simplified and Affordable
              </h2>
              <p className="max-w-3xl mx-auto text-foreground/70">
                At PayStop, we're dedicated to offering the best financial services at a price that
                works for you. Whether you're a customer or an agent, we ensure that every
                transaction is straightforward, affordable, and rewarding.
              </p>
              <Link href="/become-agent">
                <Button color="primary" size="lg">
                  Get Started
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
